import { Box, Card, CardContent, Typography } from '@mui/material';
import { format, parseISO, startOfMonth } from 'date-fns';
import { ja } from 'date-fns/locale';
import React from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DailySSMetricsV2 } from '../../../services/shelf-service';
import { TimeUnit } from './TimeUnitSelector';

interface OverallMetricsChartsProps {
  data: DailySSMetricsV2[];
  compareData?: DailySSMetricsV2[];
  showComparison?: boolean;
  timeUnit: TimeUnit;
}

const OverallMetricsCharts: React.FC<OverallMetricsChartsProps> = ({
  data,
  compareData = [],
  showComparison = false,
  timeUnit,
}) => {
  const getTimeUnitStartDate = (date: string) => {
    const dateObj = parseISO(date);
    switch (timeUnit) {
      case 'week': {
        // 全データの中で最新の日付を取得
        const allData = [...data, ...(showComparison ? compareData : [])];
        const maxDate = parseISO(
          allData.reduce(
            (max, curr) => (curr.ymd > max ? curr.ymd : max),
            allData[0].ymd,
          ),
        );
        // 最新の日付から何週目かを計算
        const diffDays = Math.floor(
          (maxDate.getTime() - dateObj.getTime()) / (1000 * 60 * 60 * 24),
        );
        const weekNumber = Math.floor(diffDays / 7);
        // 最新の日付からweekNumber週前の日付を計算
        const weekStartDate = new Date(maxDate);
        weekStartDate.setDate(weekStartDate.getDate() - weekNumber * 7);
        return format(weekStartDate, 'yyyy-MM-dd');
      }
      case 'month':
        return format(startOfMonth(dateObj), 'yyyy-MM-dd');
      default:
        return date;
    }
  };

  const processData = (metrics: DailySSMetricsV2[]) => {
    // 週次・月次表示の場合、各期間に含まれる日数と実際の日付範囲もカウント
    const groupedData = metrics.reduce(
      (acc, curr) => {
        const date = curr.ymd;
        const key = getTimeUnitStartDate(date);

        if (!acc[key]) {
          acc[key] = {
            date: key,
            shown_ss: 0,
            shown_ss_rate: 0,
            click_ss: 0,
            click_ss_rate: 0,
            shelf_cv_ss: 0,
            shelf_cv_ss_rate: 0,
            total_ss: 0,
            total_cv_ss: 0,
            dayCount: 0,
            minDate: date, // 実際の最小日付
            maxDate: date, // 実際の最大日付
          };
        }

        acc[key].shown_ss += curr.shown_ss;
        acc[key].click_ss += curr.click_ss;
        acc[key].shelf_cv_ss += curr.shelf_cv_ss;
        acc[key].total_ss += curr.ss;
        acc[key].total_cv_ss += curr.cv_ss;
        acc[key].dayCount += 1;

        // 実際の日付範囲を更新
        if (date < acc[key].minDate) acc[key].minDate = date;
        if (date > acc[key].maxDate) acc[key].maxDate = date;

        return acc;
      },
      {} as Record<
        string,
        {
          date: string;
          shown_ss: number;
          shown_ss_rate: number;
          click_ss: number;
          click_ss_rate: number;
          shelf_cv_ss: number;
          shelf_cv_ss_rate: number;
          total_ss: number;
          total_cv_ss: number;
          dayCount: number;
          minDate: string;
          maxDate: string;
        }
      >,
    );

    // 週次表示でも1週間未満のデータを含めて表示
    const filteredData = Object.values(groupedData).filter((data) => {
      if (timeUnit === 'week') {
        return data.dayCount >= 1; // 1日以上のデータがあれば表示
      }
      return true; // 日次・月次表示の場合はフィルタリングしない
    });

    // Calculate rates
    filteredData.forEach((data) => {
      if (data.total_ss > 0) {
        data.shown_ss_rate = (data.shown_ss / data.total_ss) * 100;
      }
      if (data.shown_ss > 0) {
        data.click_ss_rate = (data.click_ss / data.shown_ss) * 100;
      }
      if (data.total_cv_ss > 0) {
        data.shelf_cv_ss_rate = (data.shelf_cv_ss / data.total_cv_ss) * 100;
      }
    });

    return filteredData.sort((a, b) => a.date.localeCompare(b.date));
  };

  // データの処理
  const mainData = processData(data);
  const compareData2 = showComparison ? processData(compareData) : [];

  // 月次表示の場合は同じ月のデータを集約
  const mergeMonthlyData = (data: ReturnType<typeof processData>) => {
    if (timeUnit !== 'month') return data;

    const monthlyData = data.reduce(
      (acc, curr) => {
        const monthKey = curr.date.substring(0, 7); // YYYY-MM
        if (!acc[monthKey]) {
          acc[monthKey] = {
            ...curr,
            date: curr.date, // 月の最初の日付を保持
          };
          return acc;
        }

        // 同じ月のデータを集約
        acc[monthKey].shown_ss += curr.shown_ss;
        acc[monthKey].click_ss += curr.click_ss;
        acc[monthKey].shelf_cv_ss += curr.shelf_cv_ss;
        acc[monthKey].total_ss += curr.total_ss;
        acc[monthKey].total_cv_ss += curr.total_cv_ss;
        acc[monthKey].dayCount += curr.dayCount;

        // 実際の日付範囲を更新
        if (curr.minDate < acc[monthKey].minDate)
          acc[monthKey].minDate = curr.minDate;
        if (curr.maxDate > acc[monthKey].maxDate)
          acc[monthKey].maxDate = curr.maxDate;

        // レートを再計算
        if (acc[monthKey].total_ss > 0) {
          acc[monthKey].shown_ss_rate =
            (acc[monthKey].shown_ss / acc[monthKey].total_ss) * 100;
        }
        if (acc[monthKey].shown_ss > 0) {
          acc[monthKey].click_ss_rate =
            (acc[monthKey].click_ss / acc[monthKey].shown_ss) * 100;
        }
        if (acc[monthKey].total_cv_ss > 0) {
          acc[monthKey].shelf_cv_ss_rate =
            (acc[monthKey].shelf_cv_ss / acc[monthKey].total_cv_ss) * 100;
        }

        return acc;
      },
      {} as Record<string, ReturnType<typeof processData>[number]>,
    );

    return Object.values(monthlyData).sort((a, b) =>
      a.date.localeCompare(b.date),
    );
  };

  const allData = mergeMonthlyData(
    [...mainData, ...compareData2].sort((a, b) => a.date.localeCompare(b.date)),
  );

  // ツールチップで実際の日付範囲を参照するためのマップ
  const dateRangeMap = new Map<string, { minDate: string; maxDate: string }>();
  allData.forEach((item) => {
    dateRangeMap.set(item.date, {
      minDate: item.minDate,
      maxDate: item.maxDate,
    });
  });

  const formatXAxisTick = (date: string) => {
    switch (timeUnit) {
      case 'week':
        return format(parseISO(date), 'M/d週', { locale: ja });
      case 'month':
        return format(parseISO(date), 'yyyy/M月', { locale: ja });
      default:
        return format(parseISO(date), 'M/d', { locale: ja });
    }
  };

  const charts = [
    {
      title: 'シェルフIMPSS数',
      dataKey: 'shown_ss',
      rateKey: 'shown_ss_rate',
      color: '#2196F3',
    },
    {
      title: 'シェルフクリックSS数',
      dataKey: 'click_ss',
      rateKey: 'click_ss_rate',
      color: '#4CAF50',
    },
    {
      title: 'シェルフ経由CV SS数',
      dataKey: 'shelf_cv_ss',
      rateKey: 'shelf_cv_ss_rate',
      color: '#FF9800',
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 2,
        mb: 3,
      }}
    >
      {charts.map((chart) => (
        <Card
          key={chart.title}
          sx={{
            height: '100%',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            bgcolor: '#FFFFFF',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                fontSize: '1rem',
                mb: 2,
                textAlign: 'left',
              }}
            >
              {chart.title}
            </Typography>
            <Box sx={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <ComposedChart
                  data={allData}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 0,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="rgba(0, 0, 0, 0.1)"
                  />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatXAxisTick}
                    tick={{ fontSize: 12, fill: '#666' }}
                    tickLine={{ stroke: '#ccc' }}
                    axisLine={{ stroke: '#ccc' }}
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 12, fill: '#666' }}
                    tickLine={{ stroke: '#ccc' }}
                    axisLine={{ stroke: '#ccc' }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 12, fill: '#666' }}
                    tickLine={{ stroke: '#ccc' }}
                    axisLine={{ stroke: '#ccc' }}
                    tickFormatter={(value) => `${value}%`}
                    domain={[0, 40]}
                  />
                  <Tooltip
                    formatter={(value: number, name: string) => {
                      if (name.includes('割合')) {
                        return [`${value.toFixed(1)}%`, name];
                      }
                      return [value.toLocaleString(), name];
                    }}
                    labelFormatter={(label: string) => {
                      const date = parseISO(label);
                      const dateRange = dateRangeMap.get(label);

                      switch (timeUnit) {
                        case 'week': {
                          // 実際のデータ範囲を使用
                          if (dateRange) {
                            const startDate = parseISO(dateRange.minDate);
                            const endDate = parseISO(dateRange.maxDate);
                            return `${format(startDate, 'yyyy/M/d(E)', { locale: ja })}~${format(endDate, 'M/d(E)', { locale: ja })}`;
                          }
                          // フォールバック: 週の開始日を計算（終了日-6日）
                          const startDate = new Date(date);
                          startDate.setDate(startDate.getDate() - 6);
                          return `${format(startDate, 'yyyy/M/d(E)', { locale: ja })}~${format(date, 'M/d(E)', { locale: ja })}`;
                        }
                        case 'month': {
                          // 実際のデータ範囲を使用
                          if (dateRange) {
                            const startDate = parseISO(dateRange.minDate);
                            const endDate = parseISO(dateRange.maxDate);
                            return `${format(startDate, 'yyyy/M/d', { locale: ja })}~${format(endDate, 'M/d', { locale: ja })}`;
                          }
                          // フォールバック: 月の最後の日を計算
                          const endDate = new Date(
                            date.getFullYear(),
                            date.getMonth() + 1,
                            0,
                          );
                          return `${format(date, 'yyyy/M/d', { locale: ja })}~${format(endDate, 'M/d', { locale: ja })}`;
                        }
                        default:
                          return format(date, 'yyyy/M/d(E)', { locale: ja });
                      }
                    }}
                  />
                  <Legend
                    align="right"
                    verticalAlign="top"
                    height={36}
                    iconType="circle"
                    iconSize={8}
                  />
                  <Area
                    type="monotone"
                    dataKey={chart.dataKey}
                    name={chart.title}
                    yAxisId="left"
                    fill="#666666"
                    fillOpacity={0.2}
                    stroke="none"
                  />
                  <Line
                    type="monotone"
                    dataKey={chart.rateKey}
                    name={
                      chart.dataKey === 'click_ss'
                        ? 'クリック率'
                        : 'サイト全体に対する割合'
                    }
                    yAxisId="right"
                    stroke={chart.color}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default OverallMetricsCharts;
